import { clientFor as clientFor_ } from "#resources/lib"
import { AsyncResult, makeClient, type makeIntl, makeRunPromise } from "@effect-app/vue"
import { Commander } from "@effect-app/vue/commander"
import { Confirm } from "@effect-app/vue/confirm"
import { I18n } from "@effect-app/vue/intl"
import * as Toast_ from "@effect-app/vue/toast"
import { Toast } from "@effect-app/vue/toast"
import { WithToast } from "@effect-app/vue/withToast"
import * as Effect from "effect-app/Effect"
import { flow } from "effect-app/Function"
import * as Layer from "effect-app/Layer"
import * as ManagedRuntime from "effect/ManagedRuntime"
import { type RT } from "~/plugins/runtime"

export { AsyncResult as Result, composeQueries, makeContext, makeQueryKey, pauseWhileProcessing, useIntervalPauseWhileProcessing } from "@effect-app/vue"

export const useIntl = () => useNuxtApp().$useIntl()

export const useToast = () => {
  const toast = (color: "warning" | "error" | "success") => (message: string) => snackbar({ text: message, color })

  return {
    dismiss: (_toastId: string | number) => console.log(" dismiss is a noop here :/"), // todo
    info: toast("success"), // todo
    warning: toast("warning"),
    error: toast("error"),
    success: toast("success")
  }
}

const useRuntime = () => useNuxtApp().$runtime

// `function` declarations, NOT `export const x = <A, E>(...)`: unimport's
// export scanner mis-parses the comma in the leading generics list as a second
// declarator and registers the second type param (`E`, `F`) as a named export,
// which then fails at runtime when .nuxt/imports.mjs re-exports it.
export async function run<A, E>(
  effect: Effect.Effect<A, E, RT>,
  options?:
    | {
      readonly signal?: AbortSignal
    }
    | undefined
) {
  const context = await useRuntime().context()
  const runPromise = makeRunPromise(context)
  return await runPromise(effect, options)
}
export function runSync<A, E>(effect: Effect.Effect<A, E, RT>) {
  return useRuntime().runSync(effect)
}

let bi = BigInt(0)
const makeId = () => {
  const id = bi++
  return id.toString()
}

const intlLayer = I18n.toLayer(Effect.sync(useIntl as ReturnType<typeof makeIntl>["useIntl"]))
// TODO: use optional CurrentToastId to auto assign toastId when not null?
const toastLayer = Toast.toLayer(
  Effect.sync(() => {
    const t = useToast()
    const toast = {
      error: flow(t.error.bind(t), makeId),
      info: flow(t.info.bind(t), makeId),
      success: flow(t.success.bind(t), makeId),
      warning: flow(t.warning.bind(t), makeId),
      dismiss: flow(t.dismiss.bind(t), makeId)
    }
    return Toast_.wrap(toast)
  })
)
const commanderLayer = Commander.Default.pipe(
  Layer.provide([intlLayer, toastLayer])
)

const globalLayers = Effect.sync(() => useRuntime().globalLayers).pipe(
  Layer.unwrap
)
const viewLayers = Layer.mergeAll(intlLayer, toastLayer)
const provideLayers = Layer
  .mergeAll(
    commanderLayer,
    viewLayers,
    WithToast.Default.pipe(Layer.provide(toastLayer)),
    Confirm.Default.pipe(Layer.provide(intlLayer))
  )
  .pipe(Layer.provideMerge(globalLayers))

// argh, deprecation comments get stripped by unimport, so we group them under "Legacy" now.
const clientApi = makeClient(
  () => ManagedRuntime.make(provideLayers, { memoMap: useRuntime().memoMap }),
  clientFor_,
  Router.Default
)

export const Command = clientApi.Command
export const clientFor = clientApi.clientFor

/*
 * Handles the common "new entity" pattern where a route like /entity/new-X
 * should return a factory default instead of fetching from the API.
 * When isExisting is true, delegates to the suspense call. Otherwise
 * wraps the factory value in the same tuple shape.
 * The result data type is S | F, unifying both paths.
 * The factory returns an Effect that will be executed via `run`.
 */
export function suspenseOrFactory<S, F = S>(
  isExisting: boolean,
  suspense: () => SuspenseResult<S>,
  factory: Effect.Effect<F, any, RT>
): SuspenseResult<S | F> {
  if (isExisting) return suspense()
  return run(factory).then(
    (value) =>
      [
        computed(() => AsyncResult.success(value)),
        computed(() => value),
        () => Effect.void,
        undefined
      ] as const
  )
}

type SuspenseResult<A> = Promise<
  readonly [ComputedRef<any>, ComputedRef<A>, (...args: any[]) => any, any]
>
